import { Wrench, CheckCircle2, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

// Per-pillar metadata. Colors mirror AuditTab's PillarBreakdown so the card
// grid reads as the actionable companion to the breakdown chart.
const PILLAR_META = {
  schemaMarkup: { label: 'Schema markup', color: '#d61f97', max: 20 },
  faqStructure: { label: 'FAQ structure', color: '#ffa23d', max: 15 },
  answerCapsules: { label: 'Answer Capsules', color: '#5ac915', max: 15 },
  headingHierarchy: { label: 'Heading hierarchy', color: '#ffd106', max: 15 },
  botAccess: { label: 'Bot access', color: '#ff6a35', max: 20 },
  llmsTxt: { label: 'llms.txt', color: '#f91880', max: 10 },
  contentQuality: { label: 'Content quality', color: '#716b8b', max: 5 },
};

const PILLAR_ORDER = [
  'schemaMarkup',
  'faqStructure',
  'answerCapsules',
  'headingHierarchy',
  'botAccess',
  'llmsTxt',
  'contentQuality',
];

// One-liner shown under the pillar title. Plain English, no jargon — the
// long-form text on the audit `recommendations` field is more verbose; this
// is the scannable summary the user reads first.
const SUMMARY = {
  schemaMarkup: 'Add JSON-LD schema so AI engines can extract structured facts.',
  faqStructure: 'Add a clearly marked FAQ section with Q&A pairs.',
  answerCapsules: 'Surface 3–5 short standalone fact statements AI can quote.',
  headingHierarchy: 'Use exactly one <h1>, then proper <h2>/<h3> nesting.',
  botAccess: 'Allow AI crawlers (GPTBot, ClaudeBot, etc.) in robots.txt.',
  llmsTxt: 'Publish /llms.txt at your domain root to guide AI crawlers.',
  contentQuality: 'Add author byline, publish date, and aim for 600+ words.',
};

// Per-page failure thresholds, matching the audit engine's recommendation
// logic in backend/src/services/auditEngine.js. A page "needs this fix" when
// breakdown[pillar] is below the threshold.
const THRESHOLD = {
  schemaMarkup: 12,
  faqStructure: 10,
  answerCapsules: 9,
  headingHierarchy: 10,
  contentQuality: 3,
  // botAccess + llmsTxt are site-wide booleans — handled via health_checks.
};

// For pillars with a health_checks boolean, treat the whole site as "needing
// this fix" when the check fails — regardless of page count.
const HEALTH_KEY = {
  botAccess: 'botsAllowed',
  llmsTxt: 'llmsTxtPresent',
};

function pillarStatus(code, audit) {
  const pages = audit?.page_scores || [];
  const totalPages = pages.length;
  const healthKey = HEALTH_KEY[code];
  if (healthKey) {
    const ok = audit?.health_checks?.[healthKey];
    return { ok, affected: ok ? 0 : totalPages || 1, total: totalPages || 1 };
  }
  const threshold = THRESHOLD[code];
  if (threshold == null) return { ok: true, affected: 0, total: totalPages };
  const affected = pages.filter((p) => (p.breakdown?.[code] ?? 0) < threshold).length;
  return { ok: affected === 0, affected, total: totalPages };
}

export default function PillarFixCards({ audit, onOpenFix }) {
  const avgPillars = audit?.pillars || {};

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Wrench className="w-4 h-4" />
          </span>
          What to fix
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 mt-2">
        {PILLAR_ORDER.filter((code) => code in PILLAR_META).map((code) => {
          const meta = PILLAR_META[code];
          const status = pillarStatus(code, audit);
          const pillarScore = Math.round(avgPillars[code] ?? 0);
          return (
            <div
              key={code}
              className={clsx(
                'relative rounded-2xl border p-4 flex flex-col gap-2',
                status.ok ? 'border-emerald-100 bg-emerald-50/30' : 'border-gray-100 bg-white',
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: meta.color }}
                  aria-hidden="true"
                />
                <span className="font-semibold text-ink-900 text-sm flex-1 min-w-0">
                  {meta.label}
                </span>
                {status.ok ? (
                  <span className="pill-success inline-flex items-center gap-1 text-[11px]">
                    <CheckCircle2 className="w-3 h-3" />
                    Pass
                  </span>
                ) : (
                  <span className="pill inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-700">
                    <AlertCircle className="w-3 h-3" />
                    Fix
                  </span>
                )}
              </div>

              <div className="text-xs text-ink-500">
                <span className="font-medium text-ink-700">
                  {pillarScore}
                </span>
                <span className="text-ink-400"> / {meta.max} avg</span>
                {!status.ok && status.affected > 0 && (
                  <span className="ml-2 text-amber-700">
                    {status.affected} of {status.total} pages
                  </span>
                )}
              </div>

              <p className="text-xs text-ink-600 leading-relaxed">{SUMMARY[code]}</p>

              {!status.ok && (
                <button
                  type="button"
                  onClick={() => onOpenFix(code)}
                  className="mt-auto btn-secondary text-xs self-start"
                >
                  <Wrench className="w-3.5 h-3.5" />
                  Fix
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
